import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { ErrorToast } from '../../../adminComponents/Toasts/Toasts';
import { getAnnouncementViewers } from '../../../api/announcements.api';
import { queryKeys } from '../../../api/queryKeys';
import { handleError } from '../../../helpers/api';
import type {
  AnnouncementApp,
  AnnouncementPlatform,
} from '../../../types/api/announcements.types';
import type { UserRole } from '../../../types/user.types';

const PAGE_SIZE = 20;

export type ViewerFilters = {
  /** Already debounced and trimmed by the caller. */
  query: string;
  app: AnnouncementApp | '';
  platform: AnnouncementPlatform | '';
  role: UserRole | '';
};

export const NO_VIEWER_FILTERS: ViewerFilters = {
  query: '',
  app: '',
  platform: '',
  role: '',
};

/**
 * Everyone who saw the announcement, one row per person, newest first, paged
 * on scroll. No total comes back: a short page is the last one.
 */
export const useAnnouncementViewers = (id: number, filters: ViewerFilters) => {
  const query = useInfiniteQuery({
    queryKey: queryKeys.admin.announcements.viewers(id, filters),
    queryFn: async ({ pageParam }) => {
      const { data } = await getAnnouncementViewers(id, {
        page: pageParam,
        limit: PAGE_SIZE,
        query: filters.query || undefined,
        app: filters.app || undefined,
        platform: filters.platform || undefined,
        role: filters.role || undefined,
      });
      return data.data ?? [];
    },
    initialPageParam: 1,
    getNextPageParam: (last, pages) =>
      last.length === PAGE_SIZE ? pages.length + 1 : undefined,
  });

  const viewers = useMemo(() => query.data?.pages.flat() ?? [], [query.data]);

  // An empty table after a 500 looks exactly like nobody having seen it.
  useEffect(() => {
    if (query.error) ErrorToast(handleError(query.error));
  }, [query.error]);

  return {
    viewers,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
};
